import React from 'react';
import PropTypes from 'prop-types';
import { get, merge } from 'lodash';
import { DatePicker as FluentDatePicker } from 'office-ui-fabric-react/lib/DatePicker';
import { IconButton } from 'office-ui-fabric-react';
import theme from '../../theme.style';
import { pxToRem } from '../../utils';
import styles from './DatePicker.style';

const DatePicker = ({
  field: { name, value },
  form: { errors = {}, isSubmitting = false, setFieldValue, setFieldTouched },
  label,
  callout,
  required,
  disabled,
  className,
  showErrorMessage = true,
  onChange,
  showClearButton = false,
  ...other
}) => {
  const errorMessage = get(errors, name);
  const iconProps = { iconName: 'Cancel' };
  let datePickerStyles = styles;
  if (errorMessage) {
    datePickerStyles = merge({}, styles, {
      root: {
        selectors: {
          '& .ms-TextField-fieldGroup': {
            borderColor: theme.palette.accentError,
            borderWidth: pxToRem(1),
            backgroundColor: theme.palette.neutralWhite,
          },
        },
      },
    });
  }

  const clearDate = () => {
    setFieldValue(name, null);
    setFieldTouched(name);
  };

  const calendarProps = { className: styles.calendar };
  let placeholder = 'YYYY-MM-DD';
  if (other.monthOnly) {
    calendarProps.isDayPickerVisible = false;
    placeholder = 'YYYY-MM';
  }

  return (
    <>
      <div className={styles.flexRow}>
        <FluentDatePicker
          name={name}
          value={value && new Date(value)}
          onSelectDate={selectedDate => {
            setFieldValue(name, selectedDate);
            setFieldTouched(name);
            if (onChange) {
              onChange(name, selectedDate);
            }
          }}
          label={label}
          disabled={disabled || isSubmitting}
          placeholder={placeholder}
          borderless={disabled}
          ariaLabel={label || 'date picker'}
          className={className}
          styles={datePickerStyles}
          isRequired={required}
          calendarProps={calendarProps}
          {...other}
        />

        {showClearButton && (
          <IconButton
            iconProps={iconProps}
            disabled={disabled}
            onClick={() => clearDate()}
            ariaLabel={iconProps?.iconName}
          />
        )}
      </div>
      {showErrorMessage && errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}
    </>
  );
};

DatePicker.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    onBlur: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
  }),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  callout: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  showErrorMessage: PropTypes.bool,
  onChange: PropTypes.func,
  showClearButton: PropTypes.bool,
};

DatePicker.defaultProps = {
  className: '',
  required: false,
  label: null,
  callout: null,
  disabled: false,
  form: {
    touched: {},
    errors: {},
    isSubmitting: false,
  },
  showErrorMessage: true,
  onChange: null,
  showClearButton: false,
};

export default DatePicker;
